import React from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { MessageSquare, Clock, Tag, FileText, ChevronRight, Lightbulb } from "lucide-react";
import { LatestPostsSidebarSkeleton } from "./ForumSkeleton";
import { formatTimeAgo, findNormasCategory, RULES_DISCUSSION_ID } from "./utils";

export const LatestPostsSidebar = ({
  posts,
  loading,
  themes = [],
  onViewRules,
  rulesDiscussionId = RULES_DISCUSSION_ID,
}) => {
  const theme = useTheme();
  const isDark = theme.mode === "dark";
  const navigate = useNavigate();

  const handleViewRules = () => {
    if (typeof onViewRules === "function") {
      onViewRules();
      return;
    }

    if (rulesDiscussionId) {
      navigate(`/foro/discussion/${rulesDiscussionId}`);
      return;
    }

    const normasCategory = findNormasCategory(themes);
    if (normasCategory?.id) {
      navigate(`/foro/${normasCategory.id}`);
      return;
    }

    const el = document.getElementById("tema-normas");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div style={{ position: "sticky", top: "120px", display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* Card 1: Últimas publicaciones */}
      <div
        style={{
          backgroundColor: isDark ? "rgba(17, 18, 31, 0.85)" : (theme.colors.cardBackground || "#ffffff"),
          border: "1px solid rgba(236, 178, 54, 0.4)",
          borderRadius: "14px",
          padding: "22px 24px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          overflow: "hidden",
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <MessageSquare size={24} style={{ color: "#ECB236" }} />
            <h3
              style={{
                color: "#ffffff",
                margin: 0,
                fontSize: "var(--font-size-md, 18px)",
                fontWeight: 700,
              }}
            >
              Últimas publicaciones
            </h3>
          </div>
          <ChevronRight size={22} style={{ color: "#ECB236" }} />
        </div>

        {loading ? (
          <LatestPostsSidebarSkeleton count={4} />
        ) : posts.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "26px 12px",
              textAlign: "center",
              gap: "12px",
            }}
          >
            <FileText size={44} style={{ color: "#64748b", opacity: 0.85 }} />
            <div>
              <p style={{ margin: 0, color: "#ffffff", fontSize: "var(--font-size-body, 16px)", fontWeight: 700 }}>
                No hay publicaciones recientes
              </p>
              <p style={{ margin: "6px 0 0", color: "#94a3b8", fontSize: "var(--font-size-min, 15px)", fontWeight: 500 }}>
                ¡Sé el primero en compartir!
              </p>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {posts.map((post) => (
              <div
                key={post.id}
                onClick={() => navigate(`/foro/discussion/${post.discussion_id}`)}
                style={{
                  cursor: "pointer",
                  padding: "14px 16px",
                  borderRadius: "10px",
                  backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : "#f8fafc",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDark ? "rgba(236, 178, 54, 0.08)" : "#f1f5f9";
                  e.currentTarget.style.borderColor = "rgba(236, 178, 54, 0.3)";
                  e.currentTarget.style.transform = "translateX(3px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isDark ? "rgba(255, 255, 255, 0.03)" : "#f8fafc";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.08)";
                  e.currentTarget.style.transform = "translateX(0)";
                }}
              >
                {/* Discussion title */}
                <h4
                  style={{
                    color: "#ffffff",
                    margin: "0 0 6px 0",
                    fontSize: "var(--font-size-body, 16px)",
                    fontWeight: 700,
                    lineHeight: 1.35,
                  }}
                >
                  {post.discussion_title}
                </h4>

                {/* Author */}
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                  <span style={{ color: "#cbd5e1", fontSize: "var(--font-size-min, 15px)", fontWeight: 500 }}>
                    {post.author_name}
                  </span>
                </div>

                {/* Category + Date */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <Tag size={13} style={{ color: "#ECB236" }} />
                    <span style={{ color: "#ECB236", fontSize: "var(--font-size-min, 15px)", fontWeight: 600 }}>
                      {post.category_name}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px", flexShrink: 0 }}>
                    <Clock size={13} style={{ color: "#94a3b8" }} />
                    <span style={{ color: "#94a3b8", fontSize: "var(--font-size-min, 15px)" }}>
                      {formatTimeAgo(post.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Card 2: ¿Necesitas ayuda? (Gold unified) */}
      <div
        style={{
          backgroundColor: isDark ? "rgba(17, 18, 31, 0.85)" : (theme.colors.cardBackground || "#ffffff"),
          border: "1px solid rgba(236, 178, 54, 0.4)",
          borderRadius: "14px",
          padding: "22px 24px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
          <Lightbulb size={26} style={{ color: "#ECB236" }} />
          <h3
            style={{
              color: "#ECB236",
              margin: 0,
              fontSize: "var(--font-size-md, 18px)",
              fontWeight: 700,
            }}
          >
            ¿Necesitas ayuda?
          </h3>
        </div>

        <p
          style={{
            margin: "0 0 18px 0",
            color: "#cbd5e1",
            fontSize: "var(--font-size-min, 15px)",
            lineHeight: 1.55,
          }}
        >
          Revisa las normas del foro antes de publicar para mantener una comunidad sana y respetuosa.
        </p>

        <button
          type="button"
          onClick={handleViewRules}
          style={{
            width: "100%",
            backgroundColor: "#ECB236",
            color: "#11121f",
            border: "none",
            borderRadius: "10px",
            padding: "14px 20px",
            fontSize: "var(--font-size-body, 16px)",
            fontWeight: 700,
            fontFamily: "'Montserrat', sans-serif",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f5be47";
            e.currentTarget.style.transform = "translateY(-1px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#ECB236";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          Ver normas
        </button>
      </div>
    </div>
  );
};

export default LatestPostsSidebar;
